<template>
  <div class="w-100 my-5">
    <div class="row mx-auto">
      <div class="col-sm m-2 box-white d-flex flex-column">
        <span class="fa fa-euro-sign font-15 color-blue">&nbsp;&nbsp;&nbsp;{{ $t('ticket_price') }}</span>
        <span class="mt-3 font-bold font-50 color-blue">{{ getPriceFormat(ticketPrice) }}</span>
        <div>
          <span class="font-15 color-red">{{ getPriceFormat(ticketPriceRefunded) }}</span>
          <span class="ml-2 font-12 alpha-07">{{ $t('refunded') }}</span>
        </div>
      </div>
      <div class="col-sm m-2 box-white d-flex flex-column">
        <span class="fa fa-euro-sign font-15 color-red">&nbsp;&nbsp;&nbsp;{{ $t('service_charge') }}</span>
        <span class="mt-3 font-bold font-50 color-red">{{ getPriceFormat(serviceCharge) }}</span>
        <div>
          <span class="font-15 color-red">{{ getPriceFormat(serviceChargeRefunded) }}</span>
          <span class="ml-2 font-12 alpha-07">{{ $t('refunded') }}</span>
        </div>
      </div>
      <div class="col-sm m-2 box-white d-flex flex-column">
        <span class="fa fa-euro-sign font-15 color-blue">&nbsp;&nbsp;&nbsp;{{ $t('total_price') }}</span>
        <span class="mt-3 font-bold font-50 color-blue">{{ getPriceFormat(totalPrice) }}</span>
        <div>
          <span class="font-15 color-red">{{ getPriceFormat(totalPriceRefunded) }}</span>
          <span class="ml-2 font-12 alpha-07">{{ $t('refunded') }}</span>
        </div>
      </div>
    </div>
    <div class="row mx-auto" v-if="Object.keys(countryStats).length > 0 || Object.keys(regionStats).length > 0 || Object.keys(cityStats).length > 0">
      <div class="col-sm m-2 box-white d-flex flex-column" v-if="Object.keys(countryStats).length > 0">
        <span class="fa fa-globe font-15 color-blue">&nbsp;&nbsp;&nbsp;{{ $t('countries') }}</span>
        <span class="mt-3 font-bold font-50 color-blue">{{ Object.keys(countryStats).length }}</span>
        <div class="mt-3">
          <div class="my-1" v-for="[country, count] in getSortedStats(countryStats).slice(0, 5)" :key="country">
            <span class="font-12">{{ country }}: {{ count }}</span>
          </div>
        </div>
      </div>
      <div class="col-sm m-2 box-white d-flex flex-column" v-if="Object.keys(regionStats).length > 0">
        <span class="fa fa-map-marker font-15 color-red">&nbsp;&nbsp;&nbsp;{{ $t('regions') }}</span>
        <span class="mt-3 font-bold font-50 color-red">{{ Object.keys(regionStats).length }}</span>
        <div class="mt-3">
          <div class="my-1" v-for="[region, count] in getSortedStats(regionStats).slice(0, 5)" :key="region">
            <span class="font-12">{{ region }}: {{ count }}</span>
          </div>
        </div>
      </div>
      <div class="col-sm m-2 box-white d-flex flex-column" v-if="Object.keys(cityStats).length > 0">
        <span class="fa fa-map-marker-alt font-15 color-blue">&nbsp;&nbsp;&nbsp;{{ $t('cities') }}</span>
        <span class="mt-3 font-bold font-50 color-blue">{{ Object.keys(cityStats).length }}</span>
        <div class="mt-3">
          <div class="my-1" v-for="[city, count] in getSortedStats(cityStats).slice(0, 5)" :key="city">
            <span class="font-12">{{ city }}: {{ count }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="row mx-auto">
      <div class="col-sm m-2 box-white row vertical-center">
        <div class="col-sm my-1 vertical-center" v-if="organizerInfo">
          <img class="circle-image-30" :src="organizerInfo.thumbnailUrl || organizerInfo.photoUrl || assets.profile" />
          <span class="ml-2 font-15 font-bold">{{ organizerInfo.userName }}</span>
        </div>
        <div class="col-sm my-1 vertical-center" v-else-if="userInfo">
          <img class="circle-image-30" :src="userInfo.thumbnailUrl || userInfo.photoUrl || assets.profile" />
          <span class="ml-2 font-15 font-bold">{{ userInfo.userName }}</span>
        </div>
        <div class="col-sm my-1 vertical-center" v-else-if="eventInfo">
          <img :width="60" :height="40" :src="eventInfo.thumbnailUrl || eventInfo.photoUrl" />
          <span class="ml-2 font-15 font-bold">{{ eventInfo.name }}</span>
        </div>
        <div class="col-sm my-1 vertical-center">
          <span class="font-bold font-15 alpha-07">{{ $t('transaction') }}:</span>
          <span class="mx-5 font-bold font-20 color-blue" v-if="organizerInfo || userInfo || eventInfo">{{ getDecimalFormat(items.length) }}</span>
          <span class="mx-5 font-bold font-20 color-blue" v-else>{{ `${getDecimalFormat(items.length)}${statsMap && statsMap['transaction'] ? ` / ${getDecimalFormat(statsMap['transaction'].transactions)}` : ''}` }}</span>
        </div>
        <DatePicker
          class="col-sm my-1"
          v-model="selectedMonth"
          type="month"
          @change="onMonthChanged"
          :placeholder="$t('select_month')"
        />
        <DatePicker
          class="col-sm my-1"
          v-model="dateRange"
          type="date"
          range
          @change="onDateRangeChanged"
          :placeholder="$t('select_date_range')"
        />
        <div :class="`col-sm my-1 vertical-center ${isMobile ? '' : 'justify-content-end'}`">
          <input class="w-75" v-model="keyword" />
          <v-btn class="ml-2" icon small @click="keyword = ''" :disabled="!keyword">
            <i :class="`flaticon-circle ${keyword.length > 0 ? 'color-blue' : ''}`"></i>
          </v-btn>
        </div>
      </div>
    </div>
    <div class="row mx-auto" v-for="(item, index) in items.slice(0, Math.min((organizerInfo || userInfo || eventInfo ? pageIndex * PAGE_LIMIT_200 : items.length), items.length))" :key="(item, index)">
      <div class="col-sm m-2 p-0 box-white row">
        <div class="col-sm d-flex flex-column">
          <span class="m-2 font-12" v-if="hostname === 'localhost'">ID: {{ item.transactionId }}</span>
          <div class="my-1 vertical-center">
            <img class="circle-image-30" :src="item.organizerPhotoUrl" />
            <span class="ml-2 ellipsis font-bold font-12" :title="item.organizerName">{{ item.organizerName }}</span>
          </div>
          <img width="200px" :src="item.eventPhotoUrl" />
          <span class="my-1 w-250px ellipsis font-bold font-12" :title="item.eventName">{{ item.eventName }}</span>
        </div>
        <div class="vertical-center">
          <div class="my-5 div-divider-h" v-if="isMobile" />
          <div class="mx-3 div-divider-v" v-else />
        </div>
        <div class="col-sm-4 d-flex flex-column">
          <div :class="`my-2 vertical-center ${item.refundRequestedAt ? '' : 'pointer'}`" @click="gotoTicketList(item)">
            <span class="font-bold font-15">{{ $t('tickets') }}</span>
            <span class="ml-2 fa fa-angle-double-right font-15 color-blue" v-if="!item.refundRequestedAt"></span>
          </div>
          <div class="mt-2 d-flex justify-content-between vertical-center" v-for="(detail, index2) in item.details" :key="(detail, index2)">
            <span class="w-50 font-12" v-if="detail.eventDays">{{ `${detail.ticketName} (${$t('event_days')}: ${detail.eventDays})` }}</span>
            <span class="w-50 font-12" v-else>{{ detail.ticketName }}</span>
            <span class="ml-auto font-12">{{ `${getPriceFormat(detail.price)} * ${detail.quantity} = ${getPriceFormat(detail.price * detail.quantity)}` }}</span>
          </div>
          <span class="mt-5 font-12 color-red" v-if="item.promoCode && item.promoPercent">{{ $t('discounted_', [item.promoPercent, item.promoCode]) }}</span>
        </div>
        <div class="vertical-center">
          <div class="my-5 div-divider-h" v-if="isMobile" />
          <div class="mx-3 div-divider-v" v-else />
        </div>
        <div class="col-sm-3 d-flex flex-column">
          <div class="my-1 vertical-center">
            <img class="circle-image-30" :src="item.buyerPhotoUrl" />
            <span class="ml-2 ellipsis font-12 font-bold" :title="item.buyerName">{{ item.buyerName }}</span>
          </div>
          <span :class="`mt-2 ellipsis font-12 ${item.deviceTypeColor}`" :title="item.deviceType" v-if="item.deviceType">{{ item.deviceType }}</span>
          <span class="mt-2 ellipsis font-12 color-red" :title="item.region" v-if="item.region">{{ item.region }}</span>
          <div class="mt-5 d-flex justify-content-between">
            <span class="font-12">{{ $t('ticket_price') }}</span>
            <span class="ml-2 font-12">{{ getPriceFormat(item.ticketPrice) }}</span>
          </div>
          <div class="mt-2 d-flex justify-content-between" v-if="item.promoDiscount">
            <span class="font-12">{{ $t('promo_discount') }}</span>
            <span class="ml-2 font-12 color-red">{{ getPriceFormat(item.promoDiscount * -1) }}</span>
          </div>
          <div class="mt-2 d-flex justify-content-between">
            <span class="font-12">{{ $t('service_charge') }}</span>
            <span class="ml-2 font-12">{{ getPriceFormat(item.serviceCharge) }}</span>
          </div>
          <div class="mt-2 d-flex justify-content-between">
            <span class="font-15">{{ $t('total_price') }}</span>
            <span class="ml-2 font-bold font-15">{{ getPriceFormat(item.totalPrice) }}</span>
          </div>
          <div class="my-2 d-flex justify-content-between">
            <span class="font-12">{{ $t('handling_fee') }}</span>
            <span class="ml-2 font-12">{{ getPriceFormat(item.handlingFee || 0) }}</span>
          </div>
        </div>
        <div class="vertical-center">
          <div class="my-5 div-divider-h" v-if="isMobile" />
          <div class="mx-3 div-divider-v" v-else />
        </div>
        <div class="col-sm d-flex flex-column">
          <div class="my-2 d-flex justify-content-between vertical-center" v-if="hostname === 'localhost' && (item.transactionId.startsWith(PAYMENT_TYPE_PAYPAL_SANDBOX) || item.transactionId.startsWith(PAYMENT_TYPE_STRIPE_TEST))">
            <span class="my-2 font-bold font-15 color-red">Sandbox</span>
            <v-btn icon small @click="deleteTransactionInfoTask(item.transactionId)">
              <i class="flaticon-delete color-red"></i>
            </v-btn>
          </div>
          <span class="mt-2 font-bold font-15 color-red" v-else-if="item.refundRequestedAt">{{ $t('refund') }}</span>
          <span class="mt-2 font-bold font-15 color-blue" v-else>{{ $t('paid') }}</span>
          <div class="mt-2 d-flex flex-column" v-if="hostname === 'localhost' && item.type">
            <span class="font-12 font-bold color-red">{{ item.type }}</span>
            <span class="ml-auto font-12">{{ item.transactionId }}</span>
          </div>
          <div class="mt-2 d-flex vertical-center">
            <img height="30px" :src="item.transactionId.startsWith(PAYMENT_TYPE_PAYPAL) ? assets.paypal : assets.stripe" />
            <img class="ml-2" height="30px" :src="assets[item.paymentMethod]" v-if="item.paymentMethod && item.paymentMethod !== 'paypal'" />
            <span class="ml-auto font-12">{{ getDateStringFromTimestamp(item.createdAt) }}</span>
          </div>
          <div class="mt-2 d-flex justify-content-between" v-if="item.refundRequestedAt">
            <span class="font-12">{{ $t('requested') }}</span>
            <span class="ml-2 font-bold font-12">{{ getDateStringFromTimestamp(item.refundRequestedAt) }}</span>
          </div>
          <v-btn class="mt-5 ml-auto button-normal" small @click="requestRefundTask(item.transactionId)" v-else>
            <span>{{ $t('request_refund') }}</span>
          </v-btn>
          <div class="mt-2 d-flex justify-content-between" v-if="item.refundRequestedBy">
            <span class="font-12 color-red">{{ $t('requested_by') }}</span>
            <span class="ml-2 font-bold font-12 color-red">{{ item.refundRequestedBy }}</span>
          </div>
          <div class="mt-2 d-flex justify-content-between" v-if="item.refundCanceledBy">
            <span class="font-12 color-red">{{ $t('refund_canceled_by') }}</span>
            <span class="ml-2 font-bold font-12 color-red">{{ item.refundCanceledBy }}</span>
          </div>
          <div class="mt-5 d-flex justify-content-between" v-if="item.refundPaidAt">
            <span class="font-12">{{ $t('paid') }}</span>
            <span class="ml-2 font-12 font-bold color-red">{{ getDateStringFromTimestamp(item.refundPaidAt) }}</span>
          </div>
          <div class="mt-2 d-flex justify-content-between" v-if="item.refundPaidBy">
            <span class="font-12 color-red">{{ $t('paid_by') }}</span>
            <span class="ml-2 font-bold font-12 color-red">{{ item.refundPaidBy }}</span>
          </div>
          <div class="mt-2" v-if="isMobile">&nbsp;</div>
        </div>
      </div>
    </div>
    <div class="my-5 d-flex justify-content-center" v-if="hasMore || ((organizerInfo || userInfo || eventInfo) && items.length / PAGE_LIMIT_200 > pageIndex)">
      <v-btn text small @click="organizerInfo || userInfo || eventInfo ? pageIndex++ : getTransactionListTask()">
        <span>Load More</span>
      </v-btn>
    </div>
    <div class="py-40 center" v-if="(items.length === 0)">
      <span class="font-20 alpha-07">{{ $t('no_data_found') }}</span>
    </div>
  </div>
</template>


<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

import { httpsCallable } from 'firebase/functions';
import { functions, firestore } from '../../../main';
import { collection, query, where, orderBy, limit, startAfter, endBefore, getDocs, Timestamp } from 'firebase/firestore';
import { getPriceFormat, getDecimalFormat, getDateStringFromTimestamp, showLoading } from '../../../functions';

import profile from '@/assets/image/profile.jpg';
import paypal from '@/assets/image/paypal.png';
import stripe from '@/assets/image/stripe.png';
import card from '@/assets/image/card.png';
import sepa from '@/assets/image/sepa.png';
import sepa_debit from '@/assets/image/sepa.png';
import sofort from '@/assets/image/sofort.png';
import klarna from '@/assets/image/klarna.png';
import giropay from '@/assets/image/giropay.png';

export default {
  name: 'TransactionList',
  components: {
    DatePicker
  },
  computed: {
    isMobile() {
      return this.$store.state.isMobile;
    },
    statsMap() {
      return this.$store.state.statsMap;
    },
    organizerList() {
      return this.$store.state.organizerList || [];
    },
    organizerInfo() {
      if (this.$route.params.organizerId) {
        return this.organizerList.find(element => element.userId === this.$route.params.organizerId);
      } else {
        return null;
      }
    },
    eventList() {
      return this.$store.state.eventList || [];
    },
    eventInfo() {
      if (this.$route.params.eventId) {
        return this.eventList.find(element => element.eventId === this.$route.params.eventId);
      } else {
        return null;
      }
    },
    params() {
      return this.$route.params;
    }
  },
  watch: {
    params() {
      this.keyword = '';
      this.transactionList = [];
      this.getTransactionListTask();
    },
    eventList(newList, oldList) {
      if (oldList.length !== newList.length) {
        this.refreshData();
      }
    },
    organizerList(newList, oldList) {
      if (oldList.length !== newList.length) {
        this.refreshData();
      }
    },
    keyword() {
      this.refreshData();
    }
  },
  data() {
    return {
      assets: {
        profile,
        paypal,
        stripe,
        card,
        sepa,
        sepa_debit,
        sofort,
        klarna,
        giropay
      },
      hostname: window.location.hostname,
      selectedMonth: null,
      dateRange: null,
      ticketPrice: 0,
      serviceCharge: 0,
      totalPrice: 0,
      ticketPriceRefunded: 0,
      serviceChargeRefunded: 0,
      totalPriceRefunded: 0,
      userInfo: null,
      hasMore: false,
      pageIndex: 1,
      transactionList: [],
      cacheEventList: [],
      cacheUserList: [],
      items: [],
      keyword: '',
      countryStats: {},
      regionStats: {},
      cityStats: {}
    }
  },
  mounted() {
    this.cacheUserList = this.$store.state.userList;
    if (this.$route.params.userId) {
      this.userInfo = this.cacheUserList.find(element => element.userId === this.$route.params.userId);
    }
    this.getTransactionListTask();
  },
  methods: {
    getPriceFormat,
    getDecimalFormat,
    getDateStringFromTimestamp,
    onMonthChanged(value, type) {
      this.dateRange = null;
      this.transactionList = [];
      this.getTransactionListTask();
    },
    onDateRangeChanged(value, type) {
      if (value && value.length === 2 && value[0] && value[1]) {
        this.selectedMonth = null;
      } else {
        this.selectedMonth = new Date();
      }
      this.transactionList = [];
      this.getTransactionListTask();
    },
    refreshData() {
      var ticketPrice = 0;
      var serviceCharge = 0;
      var totalPrice = 0;
      var ticketPriceRefunded = 0;
      var serviceChargeRefunded = 0;
      var totalPriceRefunded = 0;
      const items = [];
      const keyword = this.keyword.toLowerCase();
      const countryStats = {};
      const regionStats = {};
      const cityStats = {};
      this.transactionList.filter(element => this.hostname === 'localhost' || !element.transactionId.startsWith(this.PAYMENT_TYPE_PAYPAL_SANDBOX) || !element.transactionId.startsWith(this.PAYMENT_TYPE_STRIPE_TEST)).forEach(transactionInfo => {
        const item = JSON.parse(JSON.stringify(transactionInfo));
        const buyerInfo = this.cacheUserList.find(element => element.userId === transactionInfo.buyerId);
        const organizerInfo = this.organizerList.find(element => element.userId === transactionInfo.organizerId);
        const eventInfo = this.getEventInfo(transactionInfo.eventId);
        item['buyerName'] = buyerInfo ? buyerInfo.userName : '';
        item['buyerPhotoUrl'] = buyerInfo ? buyerInfo.thumbnailUrl || buyerInfo.photoUrl || this.assets.profile : this.assets.profile;
        item['organizerName'] = organizerInfo ? organizerInfo.userName : '';
        item['organizerPhotoUrl'] = organizerInfo ? organizerInfo.thumbnailUrl || organizerInfo.photoUrl || this.assets.profile : this.assets.profile;
        item['eventName'] = eventInfo ? eventInfo.name : '';
        item['eventPhotoUrl'] = eventInfo ? eventInfo.thumbnailUrl || eventInfo.photoUrl : '';
        if (this.hostname === 'localhost') {
          item['deviceType'] = buyerInfo ? buyerInfo.deviceType : '';
          item['deviceTypeColor'] = buyerInfo ? buyerInfo.deviceTypeColor : '';
        }
        if (item.countryCode) {
          item['region'] = `${item.countryCode}, ${item.regionName || ''}, ${item.city || ''}`;
        }
        if (!keyword || !eventInfo || !buyerInfo || !organizerInfo ||
            (eventInfo && eventInfo.name.toLowerCase().includes(keyword)) ||
            (buyerInfo && buyerInfo.userName.toLowerCase().includes(keyword)) ||
            (organizerInfo && organizerInfo.userName.toLowerCase().includes(keyword))) {
          ticketPrice += transactionInfo.ticketPrice - (transactionInfo.promoDiscount || 0);
          serviceCharge += transactionInfo.serviceCharge;
          totalPrice += transactionInfo.totalPrice;
          if (transactionInfo.refundRequestedAt) {
            ticketPriceRefunded += transactionInfo.ticketPrice - (transactionInfo.promoDiscount || 0);
            serviceChargeRefunded += transactionInfo.serviceCharge;
            totalPriceRefunded += transactionInfo.totalPrice;
          }
          
          // Track location statistics
          if (transactionInfo.countryCode) {
            countryStats[transactionInfo.countryCode] = (countryStats[transactionInfo.countryCode] || 0) + 1;
          }
          if (transactionInfo.regionName) {
            regionStats[transactionInfo.regionName] = (regionStats[transactionInfo.regionName] || 0) + 1;
          }
          if (transactionInfo.city) {
            cityStats[transactionInfo.city] = (cityStats[transactionInfo.city] || 0) + 1;
          }
          
          items.push(item);
        }
      });
      items.sort((a, b) => (a.createdAt.seconds < b.createdAt.seconds) ? 1 : -1);
      this.items = items;
      this.ticketPrice = ticketPrice;
      this.serviceCharge = serviceCharge;
      this.totalPrice = totalPrice;
      this.ticketPriceRefunded = ticketPriceRefunded;
      this.serviceChargeRefunded = serviceChargeRefunded;
      this.totalPriceRefunded = totalPriceRefunded;
      this.countryStats = countryStats;
      this.regionStats = regionStats;
      this.cityStats = cityStats;
    },
    getSortedStats(statsObj) {
      return Object.entries(statsObj)
        .sort((a, b) => b[1] - a[1]);
    },
    gotoTicketList(item) {
      if (!item.refundRequestedAt) {
        this.$router.push(`/transaction/tickets/${item.transactionId}`)
      }
    },
    getEventInfo(eventId) {
      const cacheInfo = this.cacheEventList.find(element => element.eventId === eventId);
      if (cacheInfo) {
        return cacheInfo;
      } else {
        const eventInfo = this.eventList.find(element => element.eventId === eventId);
        if (eventInfo) {
          this.cacheEventList.push(eventInfo);
        }
        return eventInfo;
      }
    },
    getTransactionListTask() {
      const startDate = new Date();
      const endDate = new Date();
      if (this.selectedMonth) {
        startDate.setTime(this.selectedMonth.getTime());
        endDate.setTime(this.selectedMonth.getTime());
        startDate.setMonth(startDate.getMonth() + 1);
      } else if (this.dateRange && this.dateRange.length > 1) {
        startDate.setTime(this.dateRange[1].getTime());
        endDate.setTime(this.dateRange[0].getTime());
        startDate.setDate(startDate.getDate() + 1);
      } else {
        startDate.setDate(startDate.getDate() + 1);
        endDate.setFullYear(2022);
      }
      startDate.setHours(0);
      startDate.setMinutes(0);
      startDate.setSeconds(0);
      endDate.setHours(0);
      endDate.setMinutes(0);
      endDate.setSeconds(0);
      let startTimestamp;
      const endTimestamp = Timestamp.fromDate(endDate);
      if (this.transactionList && this.transactionList.length > 0) {
        const lastCreatedAt = this.transactionList[this.transactionList.length - 1].createdAt;
        if (lastCreatedAt && lastCreatedAt.seconds && lastCreatedAt.seconds * 1000 < startDate.getTime()) {
          startTimestamp = lastCreatedAt
        } else {
          startTimestamp = Timestamp.fromDate(startDate);
        }
      } else {
        startTimestamp = Timestamp.fromDate(startDate);
      }
      const loader = showLoading(this, 1);
      let q;
      if (this.$route.params.organizerId) {
        q = query(collection(firestore, 'transaction'), where('organizerId', '==', this.$route.params.organizerId), orderBy('createdAt', 'desc'), startAfter(startTimestamp), endBefore(endTimestamp));
      } else if (this.$route.params.userId) {
        q = query(collection(firestore, 'transaction'), where('buyerId', '==', this.$route.params.userId), orderBy('createdAt', 'desc'), startAfter(startTimestamp), endBefore(endTimestamp));
      } else if (this.$route.params.eventId) {
        q = query(collection(firestore, 'transaction'), where('eventId', '==', this.$route.params.eventId), orderBy('createdAt', 'desc'), startAfter(startTimestamp), endBefore(endTimestamp));
      } else {
        q = query(collection(firestore, 'transaction'), orderBy('createdAt', 'desc'), startAfter(startTimestamp), endBefore(endTimestamp), limit(this.PAGE_LIMIT_200));
      }
      getDocs(q).then(querySnapshot => {
        loader.hide();
        const list = [];
        var userIDs = [];
        querySnapshot.forEach(doc => {
          const data = doc.data();
          if (data.totalPrice > 0 && (this.hostname === 'localhost' || (data.organizerId !== 'Q5KyRQ4kDXNq1cbVEgpb' && !data.eventId.startsWith(this.TEST_PREFIX)))) {
            list.push(data);
            if (!this.cacheUserList.find(element => element.userId === data.buyerId)) {
              userIDs.push(data.buyerId);
              if (userIDs.length >= 30) {
                this.getUserListTask(userIDs);
                userIDs = [];
              }
            }
          }
        });
        this.hasMore = this.$route.params.organizerId || this.$route.params.userId || this.$route.params.eventId ? false : querySnapshot.docs.length === this.PAGE_LIMIT_200;
        this.transactionList = this.transactionList.concat(list);
        this.refreshData();
        if (userIDs.length > 0) {
          this.getUserListTask(userIDs);
        }
      }).catch(error => {
        loader.hide();
        this.$toast.error('Unexpected error occurs - ' + error.message);
      });
    },
    getUserListTask(userIDs) {
      const q = query(collection(firestore, 'user'), where('userId', 'in', userIDs));
      getDocs(q).then(querySnapshot => {
        const list = [];
        querySnapshot.forEach(doc => {
          const data = doc.data();
          const deviceType = data.deviceType || '';
          if (window.location.hostname === 'localhost') {
            data['deviceType'] = deviceType;
            if (deviceType.startsWith('Android : ')) {
              data['deviceTypeColor'] = 'color-red';
            } else if (deviceType.startsWith('iOS : ')) {
              data['deviceTypeColor'] = 'color-blue';
            } else if (deviceType.startsWith('Web : ')) {
              data['deviceTypeColor'] = 'color-purple';
            }
          } else {
            const myArray = deviceType.split(' : ');
            if (myArray && myArray.length > 0) {
              data['deviceType'] = myArray[0];
            } else {
              data['deviceType'] = '';
            }
            data['deviceTypeColor'] = 'color-primary';
          }
          if (data.userId === this.$route.params.userId) {
            this.userInfo = data;
          }
          list.push(data);
        });
        this.cacheUserList = this.cacheUserList.concat(list);
        this.$store.commit('set', ['userList', this.cacheUserList]);
        this.refreshData();
      });
    },
    deleteTransactionInfoTask(transactionId) {
      const params = {
        functionName: 'deleteTransactionInfo',
        transactionId: transactionId
      };
      const loader = showLoading(this, 1);
      const func = httpsCallable(functions, this.ADMIN_FUNCTION);
      func(JSON.stringify(params)).then(response => {
        loader.hide();
        if (response.data === this.RESULT_SUCCESS) {
          for (var i = this.items.length; i--;) {
            if (this.items[i].transactionId === transactionId) {
              this.items.splice(i, 1);
            }
          }
          this.$toast.success(this.$t('success'));
        } else {
          this.$toast.error(response.data);
        }
      }).catch(error => {
        loader.hide();
        this.$toast.error(error.code + ', ' + error.message);
      });
    },
    requestRefundTask(transactionId) {
      if (!transactionId) {
        this.$toast.error('Unexpected error occurs.');
        return;
      }
      if (!confirm('Are you sure you want to request the refund for this payment?')) {
        return;
      }
      const params = {
        functionName: 'requestRefund',
        transactionId: transactionId
      };
      const loader = showLoading(this, 1);
      const func = httpsCallable(functions, this.ADMIN_FUNCTION);
      func(JSON.stringify(params)).then(response => {
        loader.hide();
        if (response.data === this.RESULT_SUCCESS) {
          for (var i = this.items.length; i--;) {
            if (this.items[i].transactionId === transactionId) {
              this.items[i]['refundRequestedBy'] = 'Admin';
              this.items[i]['refundRequestedAt'] = Timestamp.now();
            }
          }
          this.$toast.success('Requested successfully.');
        } else {
          this.$toast.error(response.data);
        }
      }).catch(error => {
        loader.hide();
        this.$toast.error(error.code + ', ' + error.message);
      });
    }
  }
};
</script>